import { useEffect, useRef, useState } from 'react';
import { VITE_WEBHOOK_BASE_URL } from '@/constants/constants';
import { useCreateSplit } from '@/hooks/useCreateSplit';
import type { Bill as BillDetailsType } from '@/interfaces/Bill';
import { sendBusinessEvent } from '@/screens/Checkout/monitoring';
import type { useOutletKitchen } from '../LinkToMenu/useOutletKitchen';

interface EqualSplitPaymentParams {
  bill?: BillDetailsType;
  currency: ReturnType<typeof useOutletKitchen>['currency'];
  numberOfSplits: number;
  isSplitExist: boolean;
  isSplitLoading: boolean;
}

/**
 * The split-equally side of a payment (GK-787): registering the split on the
 * backend the moment the payer confirms it, not on the pay tap.
 *
 * Apple Pay note: Safari requires `new ApplePaySession()` to run synchronously
 * inside the user-gesture handler, and the SDK awaits `onBeforePayment` before
 * creating the session — so the pre-payment path must not await a network call.
 * Hence createSplit fires as soon as the split is confirmed (see SplitEqualMode's
 * Confirm button, which sets state.numberOfSplits via confirmSplitSettings) —
 * off the Apple Pay gesture path — and the pay button stays hidden until the
 * split exists (see CheckoutController's isPaymentReady).
 */
export const useEqualSplitPayment = ({
  bill,
  currency,
  numberOfSplits,
  isSplitExist,
  isSplitLoading,
}: EqualSplitPaymentParams) => {
  const { mutate: createSplit } = useCreateSplit();
  const [showEqualSplitCreationFailedAlert, setShowEqualSplitCreationFailedAlert] = useState(false);
  const registrationRequestedRef = useRef(false);

  useEffect(() => {
    if (numberOfSplits === 0) {
      registrationRequestedRef.current = false;
      return;
    }
    if (isSplitLoading || isSplitExist) {
      return;
    }
    if (registrationRequestedRef.current || !bill) {
      return;
    }

    registrationRequestedRef.current = true;
    createSplit(
      {
        totalAmount: bill.baseTotal,
        currency,
        reference: bill.orderNumber,
        numberOfSplits,
        type: 'SPLIT_EQUALLY',
        webhookUrl: `${VITE_WEBHOOK_BASE_URL}/api/dine-in-public/payments-api/bills/${bill.billId}/payments`,
      },
      // A failed attempt clears the guard so the next render can retry, rather
      // than wedging the pay button hidden forever
      {
        onError: () => {
          registrationRequestedRef.current = false;
          sendBusinessEvent('kitopay.split_bill.creation_failed', { splitType: 'SPLIT_EQUALLY' });
          setShowEqualSplitCreationFailedAlert(true);
          setTimeout(() => setShowEqualSplitCreationFailedAlert(false), 6000);
        },
      }
    );
  }, [numberOfSplits, isSplitLoading, isSplitExist, bill, currency, createSplit]);

  return { showEqualSplitCreationFailedAlert };
};
